import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {Address, BaseSiteService, Region, SiteConnector, User} from '@spartacus/core';
import {
  GoogleAutocomplete,
  GoogleAutocompletePlaceType,
  GoogleAutocompleteResult,
  GoogleAutocompleteService
} from '@app/services/google-autocomplete.service';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DcpValidators} from '@app/services/dcp-validators';
import {GoogleMapsService} from '@app/product/services/google-maps.service';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {finalize, switchMap, take} from 'rxjs/operators';
import {Country} from '@spartacus/core/src/model/address.model';
import {
  DcpCustomerRegistrationAddressModel,
  DcpCustomerRegistrationDecoratorService
} from '@app/services/dcp-customer-registration-decorator.service';
import {DcpApiService} from '@dcp/configurator';
import {AddressUsage} from '@app/model/order/dcp-order-entry.model';
import {HttpErrorResponse} from '@angular/common/http';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {FormValidators} from 'ng-design-system';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Subject} from 'rxjs';
import {DefaultModalService} from '@app/shared/modal/services/default-modal.service';
import {DefaultModalOptions} from '@app/shared/modal/types/default-modal.types';

@UntilDestroy()
@Component({
  selector: 'dcp-address-add-modal',
  templateUrl: './address-add-modal.component.html',
  styleUrls: ['./address-add-modal.component.scss']
})
export class AddressAddModalComponent implements OnInit, AfterViewInit {

  @Input() addressUsage: AddressUsage;
  @Input() headerText: string;
  @Input() createButtonText: string;
  @Input() openModal$: Subject<void>;
  private _addressToEdit: Address;
  get addressToEdit() {
    return this._addressToEdit;
  };

  @Input() set addressToEdit(address: Address) {
    this._addressToEdit = address;
    this.setForm(this._addressToEdit);
  };

  @Output() newAddressCreated = new EventEmitter<Address>();
  @Output() addressEdited = new EventEmitter<Address>();
  @ViewChild('streetAddress', {read: ElementRef, static: false}) streetAddressRef: ElementRef;
  @ViewChild('modalContent') modalContent: TemplateRef<any>;
  
  showErrorMessages = false;
  country: Country;
  user: User;
  addressAutocompleteRef: GoogleAutocomplete;
  regions: Region[] = [];

  inProgress = false;
  isDone = false;
  error: HttpErrorResponse;
  topModalId: string;

  errorMessages = {
    street: 'registration.error.noError',
    line2: 'registration.error.noError',
    city: 'registration.error.noError',
    regionCode: 'registration.error.noError',
    postalCode: 'registration.error.noError'
  };

  form: UntypedFormGroup = this.formBuilder.group({
    street: this.formBuilder.control(
      {value: '', disabled: this.inProgress},
      {
        validators: [
          Validators.required,
          Validators.maxLength(60),
          DcpValidators.onlyLatin1Characters(),
          DcpValidators.atLeastOneLetter(),
        ]
      }
    ),
    city: this.formBuilder.control(
      {value: '', disabled: this.inProgress},
      {
        validators: [
          Validators.required,
          Validators.maxLength(40),
          DcpValidators.onlyLatin1Characters(),
          DcpValidators.atLeastOneLetter(),
        ]
      }
    ),
    line2: this.formBuilder.control(
      {value: '', disabled: this.inProgress},
      {
        validators: [
          Validators.maxLength(40),
          DcpValidators.onlyLatin1Characters(true),
          DcpValidators.atLeastOneLetter(),
        ]
      }
    ),
    postalCode: this.formBuilder.control(
      {value: '', disabled: this.inProgress},
      {
        validators: [
          // regex validator is added dynamically
          Validators.required,
          Validators.maxLength(10),
          DcpValidators.onlyLatin1Characters()
        ]
      }
    ),
    countryCode: this.formBuilder.control(
      {value: '', disabled: this.inProgress},
      {validators: Validators.required}
    ),
    regionCode: this.formBuilder.control(
      {value: '', disabled: this.inProgress},
      {validators: Validators.required}
    ),
  }, {
    validators: [
      DcpValidators.checkMatch('street', 'city'),
      DcpValidators.checkMatch('city', 'line2'),
      DcpValidators.checkMatch('street', 'line2')
    ]
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private googleAutocompleteService: GoogleAutocompleteService,
    private googleMapService: GoogleMapsService,
    private cdr: ChangeDetectorRef,
    private siteConnector: SiteConnector,
    public countrySelectorService: CountrySelectorService,
    private apiService: DcpApiService,
    private customerRegistrationService: DcpCustomerRegistrationDecoratorService,
    private baseSiteService: BaseSiteService,
    private dcpConfigService: DcpConfigService,
    private modalService: DefaultModalService,
    @Inject(PLATFORM_ID) public platformId: string,
  ) {
  }

  ngOnInit() {
    this.country = this.countrySelectorService.getCurrentCountry();
    this.form.controls.countryCode.setValue(this.country.isocode);
    this.setupPostalCodeValidator();
    this.openModal$.pipe(
      untilDestroyed(this)
    ).subscribe(() => this.openModal());
    this.customerRegistrationService.getCurrentUser().pipe(untilDestroyed(this)).subscribe((user) => {
      this.user = user;
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    this.getRegions(this.country.isocode);
    this.googleAutocompleteService.isLoaded().then(() => {
      this.attachGoogleMapsAutocomplete();
    });
  }

  setupPostalCodeValidator() {
    this.dcpConfigService.getCountryConfig(this.country.isocode).pipe(
      take(1),
      finalize(() => {
        this.form.get('postalCode').updateValueAndValidity();
        this.cdr.detectChanges();
      }),
      untilDestroyed(this)
    ).subscribe((config) => {
      if (config.postalCodeFormat !== '') {
        this.form.get('postalCode').setValidators([
          Validators.required,
          FormValidators.regExp(config.postalCodeFormat)
        ]);
      } else {
        this.form.get('postalCode').setValidators([
          Validators.required,
          Validators.maxLength(10),
          DcpValidators.onlyLatin1Characters()
        ]);
      }
    });
  }

  getRegions(countryIsoCode: string): void {
    this.siteConnector.getRegions(countryIsoCode)
      .pipe(take(1), untilDestroyed(this))
      .subscribe((regions: Region[]) => {
        this.regions = regions;
        this.updateValidators();
        this.cdr.detectChanges();
      });
  }

  attachGoogleMapsAutocomplete(): void {
    if (this.streetAddressRef) {
      this.addressAutocompleteRef = this.googleAutocompleteService.bind(
        this.streetAddressRef,
        GoogleAutocompletePlaceType.ADDRESS,
        this.country.isocode
      );

      this.googleAutocompleteService.onChanged(
        this.addressAutocompleteRef,
        (result: GoogleAutocompleteResult) => this.onAutocompleteStreet(result)
      );
    }
  }

  closeModal(): void {
    this.modalService.closeModal(this.topModalId);
  }

  openModal(): void {
    if (this.modalContent) {
      let classByAddressUsage = '';

      switch (this.addressUsage) {
        case AddressUsage.SHIP_TO: {
          classByAddressUsage = 'checkout-access-location';
          break;
        }
        case AddressUsage.BILL_TO:
        default: {
          classByAddressUsage = 'checkout-billing-address';
        }
      }
      const extraParams: DefaultModalOptions = {
        customClasses: [classByAddressUsage],
      }
      this.topModalId = this.modalService.openModal(this.modalContent, this.headerText, undefined, extraParams);
    }
  }

  getFormControl(field: string): AbstractControl {
    return this.form.get(field);
  }

  hasMatchError(field1: string, field2: string): boolean {
    const matchErrors = [`checkMatch-${field1}:${field2}`, `checkMatch-${field2}:${field1}`];
    return this.showErrorMessages
      && !this.form.get(field1)?.errors
      && Object.keys(this.form.errors || {}).some(err => matchErrors.includes(err));
  }

  hasFieldError(field: string, errorType?: string): boolean {
    const hasError = this.showErrorMessages && this.getFormControl(field)?.errors;
    return errorType ? hasError && this.getFormControl(field)?.errors[errorType] : hasError;
  }

  isRequired(control: AbstractControl): boolean {
    if (control?.validator) {
      const validator = control.validator({} as AbstractControl);
      return validator && validator.required;
    }
    return false;
  }

  getErrorStatus(field: string): string {
    return this.showErrorMessages
    && (Object.values(this.form.errors || {}).some(({field1, field2}) => field1 === field || field2 === field)
      || this.hasFieldError(field))
      ? 'Error' : 'Standard';
  }

  setShowErrorMessages(state: boolean): void {
    this.showErrorMessages = state;
    this.validateAllInputs();
  }

  setErrorMessage(field: string): void {
    const formField = this.getFormControl(field);
    const errors = formField?.errors || {};
    let error = 'registration.error.noError';
    const matchValidatorError = this.getMatchValidationError(field);
    if (matchValidatorError || this.hasFieldError(field)) {
      const errorName = this.fieldNameParser(Object.keys(errors)[0]);
      error = `registration.error.${matchValidatorError || errorName}`;
    }
    this.errorMessages[field] = error;
  }

  getErrorMessage(field: string): string {
    return this.errorMessages[field];
  }

  isMaxLengthError(field: string): boolean {
    return this.getErrorMessage(field) === 'registration.error.maxLength';
  }

  getMatchValidationError(field: string): string {
    if (field === 'city' && (this.hasMatchError(field, 'street') || this.hasMatchError(field, 'line2'))) {
      return 'duplicatesAddressOrAddressLine2'
    }

    if (field === 'line2' && this.hasMatchError(field, 'street')) {
      return 'duplicatesAddress'
    }

    if (this.hasMatchError(field, 'line2')) {
      return 'duplicatesAddressLine2'
    }

    if (this.hasMatchError(field, 'city')) {
      return 'duplicatesCity'
    }

    return ''
  }

  getMaxLength(field: string): number {
    switch (field) {
      case 'postalCode':
        return 10;
      case 'street':
        return 60;
      default:
        return 40;
    }
  }

  validateAllInputs(): void {
    if (this.showErrorMessages) {
      Object.keys(this.errorMessages).forEach(field => this.setErrorMessage(field));
    }
  }

  private fieldNameParser(field: string): string {
    switch (field) {
      case 'maxlength':
        return 'maxLength';
      case 'onlyLatin1Characters':
        return 'nonLatin';
      case 'atLeastOneLetter':
        return 'oneLetter';
      case 'regExp':
        return 'regExpPostalCode';
      default:
        return field;
    }
  }

  trackByRegion(index: number, region: Region): string {
    return region?.isocode;
  }

  onInputChange(event: CustomEvent, field: string): void {
    const control = this.getFormControl(field);

    const target = event.target as HTMLInputElement;
    control.setValue(target.value);
    this.setErrorMessage(field);
  }

  onSelect(event: CustomEvent): void {
    this.getFormControl('regionCode').setValue([event.detail.item.value]);
  }

  onAutocompleteStreet(result: GoogleAutocompleteResult): void {
    const region: Region = this.googleMapService.getRegion(
      this.regions,
      this.googleMapService.fetchAddressType('administrative_area_level_1', result)
    );

    this.form.get('street').patchValue(
      this.googleMapService.getPlaceStreet(result)
    );
    this.form.get('city').patchValue(
      this.googleMapService.getPlaceTown(result)
    );
    this.form.get('postalCode').patchValue(
      this.googleMapService.getPlacePostalCode(result)
    );
    this.form.get('regionCode').patchValue(
      region?.isocode ? [region.isocode] : []
    );
    this.form.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  updateValidators(): void {
    if (this.regions.length > 0) {
      this.form.get('regionCode').setValidators([Validators.required]);
    } else {
      this.form.get('regionCode').clearValidators();
    }
    this.form.get('regionCode').updateValueAndValidity();
  }

  setForm(address: Address) {
    if (address) {
      this.form.get('street').patchValue(this.decodeUnicodeCharacters(address.line1));
      this.form.get('city').patchValue(this.decodeUnicodeCharacters(address.town));
      this.form.get('line2').patchValue(this.decodeUnicodeCharacters(address.line2));
      this.form.get('postalCode').patchValue(address.postalCode);
      this.form.get('regionCode').patchValue(address.region ? [address.region.isocode] : []);
      this.form.get('countryCode').patchValue(address.country.isocode);
    }
  }

  decodeUnicodeCharacters(text: string): string {
    if (!text) {
      return '';
    }
    const textArea = document.createElement('textArea');
    textArea.innerHTML = text;
    return textArea.textContent;
  }

  getFormData(): DcpCustomerRegistrationAddressModel {
    let formData: DcpCustomerRegistrationAddressModel;
    if (this.regions.length > 0) {
      formData = Object.assign(
        {},
        this.form.value,
        {
          regionCode: this.form.value.regionCode[0] || null,
        }
      );
    } else {
      this.form.removeControl('regionCode');
      formData = Object.assign(
        {},
        this.form.value,
      );
    }
    formData.line2 = formData.line2 || undefined;
    return formData;
  }

  getFormDataForUpdate(): DcpCustomerRegistrationAddressModel {
    const formData = this.getFormData();
    return {
      company: formData.company,
      countryCode: formData.countryCode,
      city: formData.city,
      street: formData.street,
      line2: formData.line2,
      postalCode: formData.postalCode,
      regionCode: formData.regionCode,
      externalAddressNumber: this._addressToEdit.externalAddressNumber,
    };
  }

  onSubmit() {
    this.error = null;
    if (this.form.invalid) {
      this.setShowErrorMessages(true);
      return;
    }
    this.inProgress = true;

    this.baseSiteService.getActive().pipe(
      take(1),
      switchMap((baseSite: string) => {
        const pathVariables = {
          baseSite,
          userId: this.user.uid,
          b2bUnitId: this.user.orgUnit.uid
        };
        const formData = this._addressToEdit ? this.getFormDataForUpdate() : this.getFormData();
        const body = {
          b2bCustomerUid: this.user.uid,
          customerId: this.user.customerId,
          addresses: [
            {
              companyName: this.user.orgUnit.name,
              countryCode: this.country.isocode,
              usage: this.addressUsage,
              ...formData,
            }
          ]
        };

        return this.apiService.callKyma<Address>('registration', 'newContactOrAddress', pathVariables, {body}, 'PATCH');
      }),
      finalize(() => {
        this.inProgress = false;
        this.cdr.detectChanges();
      }), untilDestroyed(this)
    ).subscribe((address: Address) => {
        this.isDone = true;
        if (this.isAddressEdited()) {
          this.addressEdited.next(address);
        } else {
          this.newAddressCreated.next(address);
        }
        this.closeModal();
      },
      (error: HttpErrorResponse) => {
        this.error = error;
        this.cdr.detectChanges();
      },
    );
  }

  private isAddressEdited(): boolean {
    return !!this._addressToEdit;
  }
}
